import React, { useCallback } from 'react'
import { AppTable, FlexBox, LoaderBar } from 'components'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { useNavigate } from 'react-router-dom'
import { Card } from 'semantic-ui-react'
import { useTabletAndBelowMediaQuery } from 'layout'
import {
    APPROVAL_STATUS,
    formatApprovalDate,
    isPdfStampPending,
} from './report-approval-utils'
import {
    ApprovalCardMetaRow,
    ApprovalStatusBadge,
    SectionCard,
} from './report-approval-styles'

const statusLabel = item => {
    if (isPdfStampPending(item)) return 'PDF stamp pending'
    if (item.workflowStatus === APPROVAL_STATUS.AwaitingMe) return 'Pending you'
    if (item.workflowStatus === APPROVAL_STATUS.AwaitingOthers) {
        return 'Awaiting others'
    }
    if (item.workflowStatus === APPROVAL_STATUS.Rejected) {
        return 'Rejected · needs fix'
    }
    return item.status === APPROVAL_STATUS.Approved ? 'Approved' : 'Pending'
}

const statusKey = item => {
    if (isPdfStampPending(item)) return 'awaiting_others'
    return item.workflowStatus || item.status
}

const ApprovalActionButtons = ({ item, onOpen, onApprove, onCertificate }) => {
    const approved = item.status === APPROVAL_STATUS.Approved
    const rejected = item.workflowStatus === APPROVAL_STATUS.Rejected

    return (
        <FlexBox className="justify-content-end gap-2">
            <AppSecondaryButton type="button" onClick={() => onOpen(item)}>
                {rejected ? 'View reason' : 'View'}
            </AppSecondaryButton>
            {!approved && !rejected && (
                <AppPrimaryButton type="button" onClick={() => onApprove(item)}>
                    {item.workflowStatus === APPROVAL_STATUS.AwaitingMe
                        ? 'Review & sign'
                        : 'Approve'}
                </AppPrimaryButton>
            )}
            {approved && item.approvedPdfUrl && (
                <AppSecondaryButton
                    type="button"
                    onClick={() => onCertificate(item)}
                >
                    Certificate
                </AppSecondaryButton>
            )}
        </FlexBox>
    )
}

const ApprovalCard = ({ item, onOpen, onApprove, onCertificate }) => (
    <Card fluid>
        <Card.Content>
            <Card.Header style={{ fontWeight: 600, color: '#0f172a' }}>
                {item.auditName || item.templateName || 'Untitled report'}
            </Card.Header>
            <Card.Description>
                <ApprovalCardMetaRow>
                    <span className="label">Template</span>
                    <span className="value">{item.templateName || '—'}</span>
                </ApprovalCardMetaRow>
                <ApprovalCardMetaRow>
                    <span className="label">Auditor</span>
                    <span className="value">
                        {item.auditorName || item.auditorEmail || '—'}
                    </span>
                </ApprovalCardMetaRow>
                <ApprovalCardMetaRow>
                    <span className="label">Submitted</span>
                    <span className="value">
                        {formatApprovalDate(item.completedAt)}
                    </span>
                </ApprovalCardMetaRow>
                {item.score != null && (
                    <ApprovalCardMetaRow>
                        <span className="label">Score</span>
                        <span className="value">
                            {typeof item.score === 'number'
                                ? `${item.score}%`
                                : item.score}
                        </span>
                    </ApprovalCardMetaRow>
                )}
                <ApprovalCardMetaRow>
                    <span className="label">Status</span>
                    <span className="value">
                        <ApprovalStatusBadge
                            $approved={item.status === APPROVAL_STATUS.Approved}
                            $status={statusKey(item)}
                        >
                            {statusLabel(item)}
                        </ApprovalStatusBadge>
                    </span>
                </ApprovalCardMetaRow>
                <FlexBox className="justify-content-end mt-2">
                    <ApprovalActionButtons
                        item={item}
                        onOpen={onOpen}
                        onApprove={onApprove}
                        onCertificate={onCertificate}
                    />
                </FlexBox>
            </Card.Description>
        </Card.Content>
    </Card>
)

export const ApprovalsList = ({
    items = [],
    loading = false,
    totalCount = 0,
    page = 1,
    pageSize = 20,
    onPageChange,
}) => {
    const navigate = useNavigate()
    const isMobile = useTabletAndBelowMediaQuery()

    const openReport = useCallback(
        item => {
            navigate(`/app/report-approvals/${item.completedReportId}`)
        },
        [navigate],
    )

    const approveReport = useCallback(
        item => {
            navigate(
                `/app/report-approvals/${item.completedReportId}?approve=1`,
            )
        },
        [navigate],
    )

    const openCertificate = useCallback(item => {
        if (item.approvedPdfUrl) {
            window.open(item.approvedPdfUrl, '_blank', 'noopener')
        }
    }, [])

    const showWorkflowCols = items.some(
        item => item.currentStep || item.waitingOn || item.ageLabel,
    )

    const columns = [
        {
            title: 'Audit',
            textAlign: 'left',
            render: item => (
                <strong>
                    {item.auditName || item.customerName || '—'}
                </strong>
            ),
        },
        {
            title: 'Template',
            textAlign: 'left',
            render: item => item.templateName || '—',
        },
        {
            title: 'Auditor',
            textAlign: 'left',
            render: item => item.auditorName || item.auditorEmail || '—',
        },
        {
            title: 'Submitted',
            textAlign: 'left',
            render: item => formatApprovalDate(item.completedAt),
        },
        {
            title: 'Score',
            textAlign: 'left',
            render: item => {
                if (item.score == null) return '—'
                if (typeof item.score === 'number') return `${item.score}%`
                return item.score
            },
        },
        ...(showWorkflowCols
            ? [
                  {
                      title: 'Step',
                      textAlign: 'left',
                      render: item =>
                          item.currentStep && item.totalSteps
                              ? `${item.currentStep} / ${item.totalSteps}`
                              : '—',
                  },
                  {
                      title: 'Waiting on',
                      textAlign: 'left',
                      render: item => item.waitingOn || '—',
                  },
                  {
                      title: 'Age',
                      textAlign: 'left',
                      render: item => item.ageLabel || '—',
                  },
              ]
            : []),
        {
            title: 'Status',
            textAlign: 'left',
            render: item => (
                <ApprovalStatusBadge
                    $approved={item.status === APPROVAL_STATUS.Approved}
                    $status={statusKey(item)}
                >
                    {statusLabel(item)}
                </ApprovalStatusBadge>
            ),
        },
        {
            title: 'Actions',
            textAlign: 'right',
            width: isMobile ? 'auto' : 220,
            render: item => (
                <ApprovalActionButtons
                    item={item}
                    onOpen={openReport}
                    onApprove={approveReport}
                    onCertificate={openCertificate}
                />
            ),
        },
    ]

    if (loading) {
        return <LoaderBar content="Loading reports…" />
    }

    if (!items.length) {
        return (
            <SectionCard>
                <p style={{ margin: 0, color: '#6b7280' }}>
                    No reports match your filters.
                </p>
            </SectionCard>
        )
    }

    return (
        <AppTable
            data={items}
            columns={columns}
            pageChanged={onPageChange}
            filter={{ PageNumber: page, PageSize: pageSize }}
            totalCount={totalCount}
            emptyMessage="No reports match your filters."
            mobileCard={
                <ApprovalCard
                    onOpen={openReport}
                    onApprove={approveReport}
                    onCertificate={openCertificate}
                />
            }
        />
    )
}
